import React, { useState } from "react";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import { useNavigation } from "@react-navigation/native";
import Svg, { Circle, Path } from "react-native-svg";
import { AcademicIcon, DangerIcon, ExtensionIcon, FlagIcon, Heart2Icon, InfoCircle, ThumbUpIcon } from "../assets/icons";
import { SectionHeading } from "./ReportCategoryShared";
import ReportGoodNewsCarousel from "./ReportGoodNewsCarousel";
import ReportModalSheet from "./ReportModalSheet";
import ReportEmptyState from "./ReportEmptyState";
import { academicIndicatorStatus, deriveAcademicData } from "../Utils/reportAcademicData";

function SubjectRow({ item }) {
  return (
    <View style={styles.subjectRow}>
      <View style={styles.copy}>
        <Text style={styles.subjectName}>{item.name}</Text>
        <Text style={styles.muted}>Predikat {item.grade}</Text>
      </View>
      <View style={styles.scoreColumn}>
        <Text style={styles.score}>{Math.round(item.score * 100) / 100}%</Text>
        <View style={styles.track}><View style={[styles.fill, { width: `${item.score}%` }]} /></View>
      </View>
    </View>
  );
}

export default function ReportAcademicContent({ reportId, period, mode, statistic, loading = false, loadError = false }) {
  const navigation = useNavigation();
  const [subjectsOpen, setSubjectsOpen] = useState(false);
  const [materialMode, setMaterialMode] = useState("positive");
  const attention = materialMode === "attention";
  const { subjects, subjectTotal, average: rawAverage, highest, lowest, gradeCounts, materialItems, materialGroups } =
    deriveAcademicData(statistic, period);
  const average = rawAverage === null ? 0 : Math.round(rawAverage * 100) / 100;
  const endAngle = -Math.PI / 2 - (average / 100) * 2 * Math.PI;
  const arcEndX = 34 + 26 * Math.cos(endAngle);
  const arcEndY = 34 + 26 * Math.sin(endAngle);
  const donutPath = `M 34 8 A 26 26 0 ${average > 50 ? 1 : 0} 0 ${arcEndX} ${arcEndY}`;
  const openIndicators = () => {
    navigation.navigate("ReportIndicatorDetail", {
      id: reportId, mode, period, reportType: "materials", showAllIndicators: true, indicatorGroups: materialGroups,
    });
  };

  if (loading || loadError || subjects.length === 0) {
    return (
      <ReportEmptyState
        title={loading ? "Memuat data laporan..." : loadError
          ? "Data laporan belum dapat dimuat."
          : "Belum ada data akademik untuk periode ini."}
      />
    );
  }

  return (
    <View style={styles.content}>
      <View style={styles.card}>
        <SectionHeading Icon={AcademicIcon} title="Pencapaian Akademik" actionLabel="Lihat Semua" onAction={openIndicators} />
        <View style={styles.overview}>
          <Svg width={68} height={68} viewBox="0 0 68 68" style={styles.donut}>
            <Circle cx={34} cy={34} r={26} stroke="#EEE7FF" strokeWidth={12} fill="none" />
            {average === 100 ? (
              <Circle cx={34} cy={34} r={26} stroke="#5423B5" strokeWidth={12} fill="none" />
            ) : average > 0 ? (
              <Path d={donutPath} stroke="#5423B5" strokeWidth={12} fill="none" />
            ) : null}
          </Svg>
          <View style={styles.copy}><Text style={styles.average}>{average}</Text><Text style={styles.caption}>Nilai Rata-rata</Text></View>
          <View style={styles.copy}><Text style={styles.subjectTotal}>{subjectTotal}</Text><Text style={styles.caption}>Mata Pelajaran</Text></View>
        </View>
        <View style={styles.extremes}>
          <View style={[styles.extremeColumn, styles.extremeDivider]}>
            <View style={styles.extremeHeading}>
              <View style={styles.extremeIconTile}><Text style={styles.extremeIcon}>▲</Text></View>
              <Text style={[styles.highLabel, styles.extremeLabel]}>Nilai Tertinggi</Text>
            </View>
            <Text style={styles.extremeValue}>{Math.round(highest.score * 100) / 100}<Text style={styles.muted}> • Predikat {highest.grade}</Text></Text>
            <Text style={styles.caption}>{highest.name}</Text>
          </View>
          <View style={styles.extremeColumn}>
            <View style={styles.extremeHeading}>
              <View style={styles.extremeIconTile}><Text style={styles.extremeIcon}>▼</Text></View>
              <Text style={[styles.lowLabel, styles.extremeLabel]}>Nilai Terendah</Text>
            </View>
            <Text style={styles.extremeValue}>{Math.round(lowest.score * 100) / 100}<Text style={styles.muted}> • Predikat {lowest.grade}</Text></Text>
            <Text style={styles.caption}>{lowest.name}</Text>
          </View>
        </View>
        <View style={styles.predicateHeading}>
          <View style={styles.extremeIconTile}><InfoCircle width={20} height={20} style={styles.predicateIcon} /></View>
          <Text style={styles.bold}>Predikat</Text>
        </View>
        <View style={styles.gradeGrid}>
          {[0, 1].map((column) => (
            <View style={styles.gradeColumn} key={column}>
              {gradeCounts.slice(column * 2, column * 2 + 2).map((item) => (
                <View style={styles.gradeRow} key={item.grade}>
                  <Text style={styles.gradeBadge}>{item.grade}</Text>
                  <View style={styles.copy}>
                    <Text style={styles.caption}>{item.count} Pelajaran</Text>
                    <View style={styles.gradeTrack}><View style={[styles.gradeFill, { width: `${(item.count / subjects.length) * 100}%` }]} /></View>
                  </View>
                </View>
              ))}
            </View>
          ))}
        </View>
      </View>

      <ReportGoodNewsCarousel indicatorData={materialItems} mode="materials" />

      <View style={styles.card}>
        <SectionHeading Icon={Heart2Icon} title="TOP 3 Pelajaran Terbaik" actionLabel="Lihat Semua" onAction={() => setSubjectsOpen(true)} />
        <View style={styles.divider} />
        {subjects.slice(0, 3).map((item) => <SubjectRow key={item.name} item={item} />)}
      </View>

      <View style={styles.card}>
        <SectionHeading Icon={ExtensionIcon} title="TOP 3 Materi" actionLabel="Lihat Semua" onAction={openIndicators} />
        <View style={styles.segments}>
          <Pressable accessibilityRole="button" accessibilityState={{ selected: !attention }} onPress={() => setMaterialMode("positive")} style={[styles.segment, !attention && styles.positiveBorder]}>
            <ThumbUpIcon width={22} height={22} customColor={!attention ? "#48B920" : "#151515"} />
            <Text style={[styles.segmentText, !attention && styles.highLabel]}>Materi{"\n"}Terbaik</Text>
            {!attention && <View style={[styles.underline, styles.green]} />}
          </Pressable>
          <Pressable accessibilityRole="button" accessibilityState={{ selected: attention }} onPress={() => setMaterialMode("attention")} style={[styles.segment, attention && styles.attentionBorder]}>
            <DangerIcon width={22} height={22} fill={attention ? "#D84343" : "#151515"} />
            <Text style={[styles.segmentText, attention && styles.lowLabel]}>Perlu{"\n"}Perhatian</Text>
            {attention && <View style={[styles.underline, styles.red]} />}
          </Pressable>
        </View>
        {materialItems[materialMode].map((item, index, items) => {
          const status = academicIndicatorStatus(item.value);
          return (
          <View key={`${item.category}-${item.name}`} style={[styles.materialRow, index === items.length - 1 && styles.lastRow]}>
            <View style={styles.chips}>
              <View style={styles.categoryChip}><Heart2Icon customColor="#999999" /><Text style={styles.chipText}>{item.category}</Text></View>
              <View style={[styles.statusChip, { backgroundColor: status.backgroundColor }]}><FlagIcon width={13} height={13} customColor={status.color} /><Text style={[styles.statusText, { color: status.color }]}>{status.label}</Text></View>
            </View>
            <Text style={styles.description}>{item.name}</Text>
          </View>
          );
        })}
        {materialItems[materialMode].length === 0 && (
          <ReportEmptyState title="Belum ada materi untuk kategori ini." compact />
        )}
      </View>

      <ReportModalSheet
        visible={subjectsOpen}
        onClose={() => setSubjectsOpen(false)}
        Icon={Heart2Icon}
        title="Pelajaran Terbaik"
      >
        <ScrollView style={styles.sheetList}>{subjects.map((item) => <SubjectRow key={item.name} item={item} />)}</ScrollView>
      </ReportModalSheet>
    </View>
  );
}

const styles = StyleSheet.create({
  content: { gap: 16 },
  card: { padding: 14, borderRadius: 16, backgroundColor: "#FFFFFF", elevation: 2, shadowColor: "#34234A", shadowOpacity: 0.08, shadowRadius: 4, shadowOffset: { width: 0, height: 2 } },
  overview: { flexDirection: "row", alignItems: "center", gap: 12, paddingVertical: 14 },
  donut: { flexShrink: 0 },
  copy: { flex: 1, minWidth: 0 },
  average: { color: "#111111", fontSize: 30, fontFamily: "Archivo-Bold" },
  subjectTotal: { color: "#111111", fontSize: 30, fontFamily: "Archivo-Regular" },
  caption: { color: "#171717", fontSize: 11, fontFamily: "Archivo-Regular" },
  extremes: { flexDirection: "row", borderTopWidth: 1, borderBottomWidth: 1, borderColor: "#EEEEEE" },
  extremeColumn: { flex: 1, minWidth: 0, paddingVertical: 12, paddingHorizontal: 5, gap: 5 },
  extremeDivider: { borderRightWidth: 1, borderColor: "#EEEEEE" },
  extremeHeading: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 6 },
  extremeIconTile: { width: 28, height: 28, borderRadius: 5, backgroundColor: "#F3F3F3", alignItems: "center", justifyContent: "center" },
  extremeIcon: { color: "#333333", fontSize: 14 },
  extremeLabel: { flex: 1 },
  highLabel: { color: "#48B920", fontFamily: "Archivo-Bold", fontSize: 11 },
  lowLabel: { color: "#D84343", fontFamily: "Archivo-Bold", fontSize: 11 },
  extremeValue: { color: "#111111", fontFamily: "Archivo-Bold", fontSize: 15 },
  muted: { color: "#999999", fontSize: 11, fontFamily: "Archivo-Regular" },
  predicateHeading: { flexDirection: "row", alignItems: "center", gap: 8, paddingVertical: 12 },
  predicateIcon: { transform: [{ rotate: "180deg" }] },
  bold: { color: "#171717", fontFamily: "Archivo-Bold", fontSize: 12 },
  gradeGrid: { flexDirection: "row", gap: 24 },
  gradeColumn: { flex: 1, gap: 10 },
  gradeRow: { flexDirection: "row", alignItems: "center", gap: 10 },
  gradeBadge: { width: 34, paddingVertical: 8, textAlign: "center", color: "#5423B5", backgroundColor: "#F0E9FF", borderRadius: 5, fontFamily: "Archivo-Bold", fontSize: 16 },
  gradeTrack: { height: 10, borderRadius: 4, backgroundColor: "#F1F1F1", marginTop: 4, overflow: "hidden" },
  gradeFill: { height: "100%", borderRadius: 4, backgroundColor: "#5423B5" },
  divider: { height: 1, backgroundColor: "#EEEEEE", marginVertical: 10 },
  subjectRow: { minHeight: 48, flexDirection: "row", alignItems: "center", gap: 12 },
  subjectName: { color: "#5423B5", fontFamily: "Archivo-Bold", fontSize: 12 },
  scoreColumn: { width: "32%", alignItems: "flex-end", gap: 2 },
  score: { color: "#48B920", fontFamily: "Archivo-Bold", fontSize: 11 },
  track: { height: 11, width: "100%", backgroundColor: "#EAEAEA", borderRadius: 5, overflow: "hidden" },
  fill: { height: "100%", backgroundColor: "#48B920", borderRadius: 5 },
  segments: { flexDirection: "row", gap: 8, marginTop: 12, marginBottom: 4 },
  segment: { position: "relative", flex: 1, minHeight: 52, borderRadius: 8, borderWidth: 1, borderColor: "#E7E7E7", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 8, paddingHorizontal: 8 },
  segmentText: { color: "#171717", fontFamily: "Archivo-Regular", fontSize: 11, lineHeight: 14 },
  positiveBorder: { borderColor: "#48B920" },
  attentionBorder: { borderColor: "#D84343" },
  underline: { position: "absolute", bottom: 1, left: 16, right: 16, height: 4, borderRadius: 3 },
  green: { backgroundColor: "#48B920" },
  red: { backgroundColor: "#D84343" },
  materialRow: { paddingVertical: 10, borderBottomWidth: StyleSheet.hairlineWidth, borderColor: "#EEEEEE", borderStyle: "dashed" },
  lastRow: { borderBottomWidth: 0 },
  chips: { flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 8 },
  categoryChip: { minHeight: 24, paddingHorizontal: 8, borderRadius: 5, backgroundColor: "#F1F1F1", flexDirection: "row", alignItems: "center", gap: 5 },
  chipText: { color: "#555555", fontFamily: "Archivo-Regular", fontSize: 9 },
  statusChip: { minHeight: 24, paddingHorizontal: 8, borderRadius: 5, backgroundColor: "#DDF3D5", flexDirection: "row", alignItems: "center", gap: 5 },
  attentionChip: { backgroundColor: "#FFE0E0" },
  statusText: { color: "#48B920", fontFamily: "Archivo-Regular", fontSize: 10 },
  description: { marginTop: 8, color: "#171717", fontFamily: "Archivo-Regular", fontSize: 11, lineHeight: 16 },
  sheetList: { maxHeight: 320, marginTop: 12 },
});